import { useCallback, useEffect, useMemo, useState } from 'react'
import { SeatsByStage } from './SeatsByStage'
import { useAuth } from '@/features/auth/AuthProvider'
import { useCrmSettings } from '@/features/settings/hooks/useCrmSettings'
import { EMPTY_TEMPLATE } from '@/features/settings/defaults'
import { useLeads } from './hooks/useLeads'
import { useActivity } from './hooks/useActivity'
import { useLastActivity } from './hooks/useLastActivity'
import { useAwareness } from './hooks/useAwareness'
import { addNote } from './repository/activitiesRepository'
import { subscribeToCrmUsers } from '@/features/auth/repository/crmUserRepository'
import {
  completeNextTask,
  createLead,
  saveContacts,
  updateLeadOwner,
  updateLeadProfile,
  updateLeadStage,
  updateQualification,
  verifyLead,
  archiveLead,
  updateLeadIdentity,
  type LeadProfilePatch,
  type NewLeadInput,
} from './repository/leadsRepository'
import { awarenessBadge, buildSteps, templateKeyFor } from './pipeline'
import { matchesLeadSearch } from './search'
import { ListMap, type MapRecord } from '@/shared/map/LazyListMap'
import { ViewToggle, useViewMode } from '@/shared/components/ViewToggle'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import { KanbanBoard, type KanbanCard } from '@/shared/components/KanbanBoard'
import {
  buildCells,
  COLUMN_KEYS,
  DEFAULT_COLUMNS,
  passesFilters,
  proStateLabel,
  sortLeads,
  type ColumnFilters,
  type LeadProState,
  type ColumnKey,
  type SortDir,
  type SortKey,
} from './table'
import {
  LeadTable,
  ColumnPicker,
  type FilterOptions,
  type TableRow,
} from './components/LeadTable'
import { LeadDrawer } from './components/LeadDrawer'
import { VerifyPanel } from './components/VerifyPanel'
import { LeadIdentity } from './components/LeadIdentity'
import { LeadOverview } from './components/LeadOverview'
import { ProCompanyCard } from './components/ProCompanyCard'
import { useProCompanies } from '@/features/dashboards/useProCompanies'
import { linkedCompanyId } from '@/features/dashboards/proLeadMatch'
import { NextStep } from './components/NextStep'
import { ContactList } from './components/ContactList'
import { Disclosure } from '@/shared/ui/Disclosure'
import { ProcessStepper } from './components/ProcessStepper'
import { LeadFlags } from './components/LeadFlags'
import { AlsoInOwnership } from '@/features/ownership/AlsoInOwnership'
import { NextFollowUpCard } from './components/NextFollowUpCard'
import { QualificationCard } from './components/QualificationCard'
import { EnrichmentCard } from './components/EnrichmentCard'
import { QualificationModal } from './components/QualificationModal'
import { GuideStatusTable } from './components/GuideStatusTable'
import { ActivityPanel } from './components/ActivityPanel'
import { NewLeadModal } from './components/NewLeadModal'
import { LeadFields } from './components/LeadFields'
import { ContactModal } from './components/ContactModal'
import {
  qualificationScore,
  rolesOf,
  type Contact,
  type FollowUpTask,
  type CrmUser,
  type Lead,
  type Qualification,
} from '@/types/crm'
import { scoreStyle } from './qualification'
import { formatDate, formatYear } from '@/shared/util/dates'
import './LeadsWorkspace.css'

/**
 * Shown on a Prospect lead while the consumer-Skida cross-check is still in
 * flight — which of the two Prospect templates applies is not known yet.
 */
const PENDING_TEMPLATE = {
  title: 'Checking whether they already know Skida…',
  guidance:
    'The outreach template for a Prospect depends on whether anyone at the company already uses the consumer Skida app. One moment.',
  subjectNo: '',
  bodyNo: '',
  subjectEn: '',
  bodyEn: '',
}

/** Editing target for the contact modal: an index, or null for "new". */
type ContactTarget = { index: number | null } | null

/**
 * The lane a lead with no owner sits in — decision 102's pool.
 *
 * `Lead.owner` is the string `'Unassigned'` in the data already, so this is that
 * string named rather than a new state. Kept as a constant because the lane key,
 * the lane label and the reassignment all have to agree on it.
 */
const UNASSIGNED = 'Unassigned'

/** Column choice is a per-rep preference, so it survives a reload. */
const COLUMNS_KEY = 'skidaCrm.leadColumns'

function loadColumns(): ColumnKey[] {
  try {
    const raw = localStorage.getItem(COLUMNS_KEY)
    if (!raw) return DEFAULT_COLUMNS
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return DEFAULT_COLUMNS
    // Drop anything that is no longer a column, so an old preference cannot
    // crash the table after a rename.
    const valid = parsed.filter((k): k is ColumnKey => COLUMN_KEYS.includes(k as ColumnKey))
    if (!valid.length) return DEFAULT_COLUMNS
    // PRO state arrived on 22 Oct 2026. A saved choice predates it, so it is
    // added once, beside PRO users; after that, switching it off sticks.
    if (!localStorage.getItem(`${COLUMNS_KEY}.proState`)) {
      localStorage.setItem(`${COLUMNS_KEY}.proState`, '1')
      if (!valid.includes('proState')) {
        const at = valid.indexOf('proUsers')
        valid.splice(at >= 0 ? at + 1 : valid.length, 0, 'proState')
      }
    }
    return valid
  } catch {
    return DEFAULT_COLUMNS
  }
}

/**
 * One lead as a Kanban card.
 *
 * Same three facts the table leads with — who they are, where they are, what
 * they scored — because the toggle is meant to rearrange the list, not to show
 * a different one.
 */
function toKanbanCard(lead: Lead, lastActivity?: string): KanbanCard {
  /**
   * Three lines, and decision 102 names them: company, contact person, when the
   * lead was last touched.
   *
   * What it takes *off* the card matters as much. The score pill went because
   * this board is read to decide who does what next, not to rank the book — and
   * the owner went because the lane the card sits in already says whose it is.
   * Repeating it inside the card is noise on every card in the row.
   */
  const contact = lead.contacts.find((c) => c.name.trim())?.name ?? ''
  return {
    id: lead.id,
    group: lead.stage,
    lane: lead.owner || UNASSIGNED,
    title: lead.name,
    // The contact, not the region: on a distribution board the useful second
    // line is the person somebody would ring.
    meta: contact || 'No contact named',
    badge: null,
    trailing: lastActivity ? formatDate(lastActivity) : '—',
  }
}

export function LeadsWorkspace({ onOpenAdmin }: { onOpenAdmin: () => void }) {
  const { user } = useAuth()
  const settings = useCrmSettings()
  const { leads, loading, error } = useLeads()
  /**
   * The Skida PRO companies, read once, for the PRO state column and the
   * Skida PRO card — 22 Oct 2026. The same loader and state rules as the PRO
   * dashboard, so the two screens cannot disagree.
   */
  const pro = useProCompanies()
  const proOf = useCallback(
    (lead: Lead): LeadProState => {
      const id = linkedCompanyId(lead)
      if (!id) return 'unlinked'
      return pro.byId.get(id)?.state ?? 'unknown'
    },
    [pro],
  )

  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [sortBy, setSortBy] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [columns, setColumns] = useState<ColumnKey[]>(loadColumns)
  const [showColumnPicker, setShowColumnPicker] = useState(false)
  const [filters, setFilters] = useState<ColumnFilters>({})
  const [openFilter, setOpenFilter] = useState<ColumnKey | null>(null)
  const [showHidden, setShowHidden] = useState(false)
  const [viewMode, setViewMode] = useViewMode()
  const isMobile = useIsMobile()
  /** The chip is a shortcut to the `mine` search token, not a second filter. */
  const [mineOnly, setMineOnly] = useState(false)
  const [showNewLead, setShowNewLead] = useState(false)
  const [showQual, setShowQual] = useState(false)
  const [contactTarget, setContactTarget] = useState<ContactTarget>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(columns))
  }, [columns])

  // `?lead=<id>` opens that lead — the Skida PRO dashboard links its companies
  // here (21 Oct 2026). Read once the leads are in, then taken out of the
  // address so a reload lands on the list as usual.
  useEffect(() => {
    if (loading) return
    const id = new URLSearchParams(window.location.search).get('lead')
    if (!id) return
    window.history.replaceState(null, '', window.location.pathname)
    if (leads.some((l) => l.id === id)) {
      setSelectedId(id)
      setDrawerOpen(true)
    }
  }, [loading, leads])

  const currentUser = user?.displayName ?? ''

  /**
   * Display name → sign-in address, from the allowlist.
   *
   * The owner picker has always worked in display names and every screen shows
   * them, but `ownerEmail` is what the external-sales rule matches, so a
   * reassignment has to resolve one to the other. A name with no row on the
   * list resolves to `''`, which is a lead no external caller can see — the
   * safe direction, and visible as "not on the allowlist" rather than silent.
   */
  const [crmUsers, setCrmUsers] = useState<CrmUser[]>([])
  useEffect(
    () =>
      subscribeToCrmUsers(
        (rows) => setCrmUsers(rows),
        () => setCrmUsers([]),
      ),
    [],
  )
  const emailForOwner = useCallback(
    (name: string) =>
      crmUsers.find((u) => u.displayName.trim().toLowerCase() === name.trim().toLowerCase())
        ?.email ?? '',
    [crmUsers],
  )

  /**
   * Everybody a lead can be given to — 31 Aug 2026.
   *
   * This was built from the leads themselves, which meant the picker only ever
   * offered people who *already owned one*. A new colleague could not be given
   * their first lead: they were not in the list until they were in the list.
   *
   * So it comes from the allowlist now — every team member who works leads,
   * whether or not they hold any. External sales are included deliberately:
   * assigned leads are the whole of what that role can see, so leaving them out
   * would make the role unusable. Shareholders and ambassadors are not, having
   * no access to the pipeline to assign into.
   *
   * Union with the owners already on leads, so somebody who has left — off the
   * allowlist, still named on their old book — does not vanish from the picker
   * and silently reassign their leads to whoever is first alphabetically.
   */
  const owners = useMemo(() => {
    const canWorkLeads = (u: CrmUser) => {
      if (u.disabled) return false
      const roles = rolesOf(u)
      return roles.some((r) => r === 'admin' || r === 'sales' || r === 'externalSales')
    }
    const names = new Set<string>()
    for (const u of crmUsers) {
      if (canWorkLeads(u) && u.displayName.trim()) names.add(u.displayName.trim())
    }
    for (const l of leads) if (l.owner) names.add(l.owner)
    names.delete(UNASSIGNED)
    return [...names].sort((a, b) => a.localeCompare(b))
  }, [crmUsers, leads])

  const lastActivity = useLastActivity()

  /**
   * The lanes, in the order decision 102 sets: the pool, then the people.
   *
   * Drawn from who actually owns leads, plus the signed-in user — a board you
   * cannot hand work to yourself on is missing the lane you reach for most, and
   * a new rep with an empty book would otherwise have nowhere to be given any.
   */
  const laneList = useMemo(() => {
    // Every team member, not only those already holding leads — 31 Aug 2026.
    // A lane is how work is handed over on this board, so a colleague without
    // one cannot be given anything, which is the same gap the owner picker had.
    // 102 said "every CRM user who owns something, plus you"; that row wants
    // amending to say every team member.
    const names = new Set(owners)
    if (currentUser) names.add(currentUser)
    names.delete(UNASSIGNED)
    return [
      { key: UNASSIGNED, label: 'Unassigned', pool: true },
      ...[...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ key: n, label: n })),
    ]
  }, [owners, currentUser])

  /**
   * A drop: reassign, move the stage, or both — decision 102.
   *
   * Each half is written only when it changed, and each is logged in the wording
   * the build already uses, so the board and the drawer can never disagree about
   * what happened. Logged as `action` rather than `user`: nobody typed these,
   * they are the record of a control being used.
   *
   * The lead is read from `leads` rather than from the card, because the card is
   * a projection and the write needs the record.
   */
  const reassign = useCallback(
    async (id: string, to: { column: string; lane: string }) => {
      const lead = leads.find((l) => l.id === id)
      if (!lead || !user) return
      const owner = to.lane
      const stage = to.column
      try {
        if (owner !== lead.owner) {
          await updateLeadOwner(id, owner, emailForOwner(owner))
          await addNote(id, user.displayName, `Owner changed to ${owner}.`, 'action')
        }
        if (stage !== lead.stage) {
          // The stage being left is passed on purpose: `updateLeadStage` uses it
          // to keep `wonAt` when a lead leaves and re-enters Won, and getting it
          // wrong would silently re-date a win.
          await updateLeadStage(id, stage, lead.stage)
          // The build's own wording for a stage change — see `onStageChange`.
          await addNote(
            id,
            user.displayName,
            `Moved to ${settings.findStage(stage).label}.`,
            'action',
          )
        }
      } catch {
        // The board is live on `leads`, so a failed write simply leaves the card
        // where it was — which is the truth, and better than a card that has
        // moved on screen and nowhere else.
      }
    },
    [leads, user, settings, emailForOwner],
  )

  const stageOrder = useMemo(() => settings.allStageDefs.map((s) => s.key), [settings])

  /**
   * Which held-back stages this view is allowed to show.
   *
   * `unverified` has its own toggle, because Step 0 is a job somebody sits down
   * to do. `archived` and `do-not-contact` deliberately have none: the only way
   * to see them is to tick the stage in the Stage column filter or name it in
   * the search box. Archived leads must never come back by accident, and a
   * do-not-contact lead must never appear in a list being worked down.
   */
  const revealed = useMemo(() => {
    const out = new Set<string>()
    if (showHidden) out.add('unverified')
    for (const k of filters.stage ?? []) if (settings.hiddenStageKeys.has(k)) out.add(k)
    for (const k of settings.hiddenStageKeys) {
      if (search.toLowerCase().includes(settings.findStage(k).label.toLowerCase())) out.add(k)
    }
    return out
  }, [showHidden, filters.stage, settings, search])

  const searched = useMemo(
    () =>
      leads
        .filter((l) => matchesLeadSearch(l, search, { currentUser, stages: settings.allStageDefs }))
        .filter(
          (l) =>
            !mineOnly ||
            matchesLeadSearch(l, 'mine', { currentUser, stages: settings.allStageDefs }),
        )
        .filter((l) => passesFilters(l, filters, proOf(l))),
    [leads, search, currentUser, settings, filters, mineOnly, proOf],
  )

  /** Leads waiting in Step 0 — what the toolbar button offers to open. */
  const unverifiedCount = useMemo(
    () => searched.filter((l) => l.stage === 'unverified').length,
    [searched],
  )

  const filtered = useMemo(() => {
    const visible = searched.filter(
      (l) => !settings.hiddenStageKeys.has(l.stage) || revealed.has(l.stage),
    )
    return sortLeads(visible, sortBy, sortDir, stageOrder, proOf)
  }, [searched, revealed, settings, sortBy, sortDir, stageOrder, proOf])

  // Filter options come from the whole book, not the filtered view — otherwise
  // ticking "Norway" would remove every other country from its own dropdown.
  const filterOptions: FilterOptions = useMemo(
    () => ({
      stage: settings.allStageDefs.map((s) => ({ value: s.key, label: s.label })),
      country: [...new Set(leads.map((l) => l.country).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((c) => ({ value: c, label: c })),
      proState: (['signedIn', 'registered', 'trial', 'signedUp', 'abandoned', 'unlinked'] as LeadProState[]).map(
        (v) => ({ value: v, label: proStateLabel(v) }),
      ),
    }),
    [leads, settings],
  )

  /**
   * The filtered leads, as the map needs them — decision 84's one filtered set.
   *
   * Colour is the pipeline stage (88), taken from the same stage definitions the
   * board colours its columns with, so a lead is one colour everywhere.
   *
   * Most of these are placed from a country or a region, because that is all a
   * lead record holds. They will be hollow pins, which is the honest reading:
   * we know the country, not the doorway.
   */
  const mapRecords = useMemo(
    (): MapRecord[] =>
      filtered.map((lead) => {
        const def = settings.allStageDefs.find((st) => st.key === lead.stage)
        return {
          id: lead.id,
          title: lead.name,
          subtitle: [lead.region, lead.country].filter(Boolean).join(' · '),
          color: def?.color ?? '#4c5b82',
          statusLabel: def?.label ?? lead.stage,
          geo: lead.geo,
          missingReason: lead.country || lead.region ? 'could not be placed' : 'no country or region',
        }
      }),
    [filtered, settings.allStageDefs],
  )

  const toggleFilter = useCallback((key: ColumnKey, value: string) => {
    setFilters((prev) => {
      const current = prev[key] ?? []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      return { ...prev, [key]: next }
    })
  }, [])

  const clearFilter = useCallback((key: ColumnKey) => {
    setFilters((prev) => ({ ...prev, [key]: [] }))
  }, [])

  const activeFilterCount = useMemo(
    () => Object.values(filters).reduce((n, values) => n + (values?.length ?? 0), 0),
    [filters],
  )

  // The selection is only meaningful while the drawer is open; a lead filtered
  // out from under it closes the drawer rather than silently swapping in a
  // different company.
  const selected = useMemo(
    () => filtered.find((l) => l.id === selectedId) ?? null,
    [filtered, selectedId],
  )

  useEffect(() => {
    if (drawerOpen && !selected) setDrawerOpen(false)
  }, [drawerOpen, selected])

  // Cross-check only the visible leads' contacts. Over 300 leads the old
  // whole-book lookup was hundreds of reads on every load.
  const visibleEmails = useMemo(
    () => filtered.flatMap((l) => l.contacts.map((c) => c.email)).filter(Boolean),
    [filtered],
  )
  const { awareness, pending: awarenessPending } = useAwareness(visibleEmails)

  const knowsSkida = useCallback(
    (contacts: Contact[]) =>
      contacts.some(
        (c) =>
          awareness.get(c.email.trim().toLowerCase())?.isConsumerUser === true ||
          c.knownSkidaUser === true,
      ),
    [awareness],
  )

  const rows: TableRow[] = useMemo(
    () =>
      filtered.map((lead) => {
        const stage = settings.findStage(lead.stage)
        return {
          id: lead.id,
          name: lead.name,
          logoUrl: lead.logoUrl,
          metaLine: [lead.region, lead.website].filter(Boolean).join(' · '),
          flag: lead.flags.some((f) => f.level === 'critical')
            ? 'critical'
            : lead.flags.some((f) => f.level === 'warning')
              ? 'warning'
              : null,
          cells: buildCells(lead, columns, stage, proOf(lead)),
          needsVerifying: lead.stage === 'unverified',
          // The phone card, which replaces the one-column table this screen used
          // to fall back to below 760px. Who owns it and what is next are the two
          // things worth carrying; the stage arrives as the pill.
          cardLines: [
            lead.owner ? `Owner: ${lead.owner}` : 'Unassigned',
            lead.nextTask?.text || 'No next step',
          ],
        }
      }),
    [filtered, columns, settings, proOf],
  )

  const [rowBusy, setRowBusy] = useState<string | null>(null)

  /**
   * Step 0, done from the row.
   *
   * The activity note is written after the move so the log records who decided
   * and when — a lead that reappears in a later import can then be recognised
   * as already-judged rather than new.
   */
  const handleVerify = useCallback(
    async (id: string) => {
      const lead = leads.find((l) => l.id === id)
      if (!lead) return
      setRowBusy(id)
      try {
        await verifyLead(lead)
        if (user) await addNote(id, user.displayName, 'Verified — accepted into the pipeline.', 'action')
      } finally {
        setRowBusy(null)
      }
    },
    [leads, user],
  )

  const handleArchive = useCallback(
    async (id: string) => {
      setRowBusy(id)
      try {
        await archiveLead(id)
        if (user) {
          await addNote(id, user.displayName, 'Archived during verification — not a lead we will work.', 'action')
        }
      } finally {
        setRowBusy(null)
      }
    },
    [user],
  )

  const handleSaveIdentity = useCallback(
    async (patch: { website: string; country: string; region: string }) => {
      if (!selectedId) return
      await updateLeadIdentity(selectedId, patch)
    },
    [selectedId],
  )

  const { activity } = useActivity(selected?.id ?? null)

  // Clicking the active column flips direction; clicking another starts ascending.
  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortBy) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      else {
        setSortBy(key)
        setSortDir('asc')
      }
    },
    [sortBy],
  )

  const toggleColumn = useCallback((key: ColumnKey) => {
    setColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }, [])

  const handleAddNote = useCallback(
    async (text: string) => {
      if (!selected || !user) return
      await addNote(selected.id, user.displayName, text)
    },
    [selected, user],
  )

  const handleComplete = useCallback(
    async (task: FollowUpTask) => {
      if (!selected || !user) return
      await completeNextTask(selected.id, task)
      await addNote(selected.id, user.displayName, `Completed follow-up: ${task.text}`, 'action')
    },
    [selected, user],
  )

  const handleStageChange = useCallback(
    async (stage: string) => {
      if (!selected || !user || stage === selected.stage) return
      const label = settings.findStage(stage).label
      await updateLeadStage(selected.id, stage, selected.stage)
      await addNote(selected.id, user.displayName, `Moved to ${label}.`, 'action')
    },
    [selected, user, settings],
  )

  const handleOwnerChange = useCallback(
    async (owner: string) => {
      if (!selected || !user || owner === selected.owner) return
      await updateLeadOwner(selected.id, owner, emailForOwner(owner))
      await addNote(selected.id, user.displayName, `Owner changed to ${owner}.`, 'action')
    },
    [selected, user, emailForOwner],
  )

  const handleCreateLead = useCallback(
    async (input: NewLeadInput) => {
      const id = await createLead(input)
      setSelectedId(id)
      setDrawerOpen(true)
      if (user) await addNote(id, user.displayName, 'Lead created.', 'action')
    },
    [user],
  )

  const handleSaveProfile = useCallback(
    async (patch: LeadProfilePatch) => {
      if (!selected) return
      const stageChanged = patch.stage !== selected.stage
      await updateLeadProfile(selected, patch)
      if (user && stageChanged) {
        await addNote(
          selected.id,
          user.displayName,
          `Moved to ${settings.findStage(patch.stage).label}.`,
          'action',
        )
      }
    },
    [selected, user, settings],
  )

  const handleSaveQualification = useCallback(
    async (qualification: Qualification) => {
      if (!selected || !user) return
      await updateQualification(selected.id, qualification)
      const score = [
        qualification.hasVolume,
        qualification.hasDigitalTools,
        qualification.highSpend,
      ].filter(Boolean).length
      await addNote(selected.id, user.displayName, `Qualification updated to ${score}/3.`, 'action')
    },
    [selected, user],
  )

  const handleSaveContact = useCallback(
    async (contact: Contact) => {
      if (!selected || contactTarget === null) return
      const next = [...selected.contacts]
      if (contactTarget.index === null) next.push(contact)
      else next[contactTarget.index] = contact
      await saveContacts(selected, next)
    },
    [selected, contactTarget],
  )

  const handleRemoveContact = useCallback(
    async (index: number) => {
      if (!selected) return
      // Confirmation is asked for inline on the row itself; see ContactList.
      // The label is still needed for the activity note.
      const contact = selected.contacts[index]
      const label = contact?.name || contact?.email || 'contact'
      setBusy(true)
      try {
        await saveContacts(
          selected,
          selected.contacts.filter((_, i) => i !== index),
        )
        if (user) await addNote(selected.id, user.displayName, `Removed contact ${label}.`, 'action')
      } finally {
        setBusy(false)
      }
    },
    [selected, user],
  )

  if (error) {
    return (
      <div className="workspace-error">
        <strong>Could not load leads.</strong>
        <p>{error}</p>
        <p className="micro">
          “Missing or insufficient permissions” means your account may not read leads. Ask an
          administrator to check your role under Admin.
        </p>
      </div>
    )
  }

  const stageDef = selected ? settings.findStage(selected.stage) : null
  const selectedAware = selected ? knowsSkida(selected.contacts) : false
  const badge = selected ? awarenessBadge(selected.stage, selectedAware, awarenessPending) : null
  const awaitingAwareness = Boolean(selected && selected.stage === 'prospect' && awarenessPending)
  const template = !selected
    ? EMPTY_TEMPLATE
    : awaitingAwareness
      ? PENDING_TEMPLATE
      : (settings.templates[templateKeyFor(selected.stage, selectedAware)] ?? EMPTY_TEMPLATE)

  /**
   * The lead detail, in one place for both of the ways it is shown.
   *
   * In the table on a desktop it opens inside the row it belongs to, so the
   * list never leaves the screen — a rep comparing four leads is not made to
   * open and close four drawers. Everywhere else, a drawer over the top: on
   * the map and the board there is no row to open into, and on a phone the
   * panel is the whole screen either way.
   *
   * Same body, same components, same handlers. Two layouts of one detail is
   * how they drift apart.
   */
  const detailBody =
    selected && stageDef ? (
      <>
          {/* Order follows how a lead is actually worked, restated 8 Sep 2026:
              what is wrong with it, who it is, its own details, what has
              happened on it, why it is worth a call, the call itself — and only
              then the reference material, all of it folded away. */}
          <LeadFlags flags={selected.flags} />
          {/* Admin-only, and it renders nothing at all otherwise — decision 13.
              A gap where a tag would have been is still a disclosure. */}
          <AlsoInOwnership leadId={selected.id} />

          {selected.stage === 'unverified' && (
            <VerifyPanel
              lead={selected}
              onSave={handleSaveIdentity}
              onVerify={() => handleVerify(selected.id)}
              onArchive={() => handleArchive(selected.id)}
            />
          )}

          <LeadIdentity lead={selected} awareness={badge} />

          {/*
            The editable fields, high up — asked for on 4 Sep 2026, and right:
            the panel is a form per company, so the form should not be the last
            thing on it. Above the activity log and the call, because everything
            below is either an action or reference material and the fields are
            neither.

            Not at Step 0, where `VerifyPanel` above is already the form: it
            carries the website, the country and the region with Verify and
            Archive beside them, because triage is a different job from
            maintenance. Two forms holding the same three fields would be the
            duplication this change exists to remove.

            `key` remounts the form when a different company opens — the draft is
            seeded once and never re-seeded, so a live snapshot cannot overwrite
            half-typed input. See `LeadFields`.
          */}
          {selected.stage !== 'unverified' && (
            <LeadFields
              key={selected.id}
              lead={selected}
              owners={owners}
              currentUser={currentUser}
              onSave={handleSaveProfile}
              onOwnerChange={(o) => void handleOwnerChange(o)}
            />
          )}

          {/*
            **The log, third — asked for on 8 Sep 2026.**

            It was the last disclosure on the panel and closed by default, so
            the two most frequent acts on a lead — writing down what just
            happened, and reading where it was left — were both a scroll past
            everything else and a click. Directly under the fields now, open,
            with the composer on the left and the last few entries on the right.
            `ActivityPanel` carries the reasoning for the split and for showing
            only three.
          */}
          <ActivityPanel activity={activity} onAddNote={handleAddNote} />

          <LeadOverview
            lead={selected}
            seatGap={selected.seatsActive > 0 && selected.seatsBought === 0}
          />

          <ProCompanyCard lead={selected} pro={pro} />

          {/*
            **Who to call, beside the call itself.**

            Contacts came out of the folded reference material to get here. It
            was never reference: the next step block writes an email to
            `contacts[0]`, and the name it is addressed to was a disclosure away
            from the draft. Side by side, picking the right person and sending
            to them is one glance.

            The stepper rides with the next step rather than standing on its own
            — where the lead is and where it goes next are the same question,
            and the stepper alone above a full-width rule was a label pretending
            to be a section.
          */}
          <div className="detail__split">
            <section className="card">
              <div className="card-label">Contacts</div>
              <ContactList
                contacts={selected.contacts.map((c) => {
                  const hit = awareness.get(c.email.trim().toLowerCase())
                  return {
                    ...c,
                    awareness: hit
                      ? { ...hit, since: hit.since ? formatYear(hit.since) || hit.since : undefined }
                      : undefined,
                  }
                })}
                onAdd={() => setContactTarget({ index: null })}
                onEdit={(index) => setContactTarget({ index })}
                onRemove={(index) => void handleRemoveContact(index)}
                busy={busy}
              />
            </section>

            <div className="detail__nextcol">
              <ProcessStepper
                steps={buildSteps(selected.stage, settings.stages, settings.outcomes)}
              />

              <NextStep
                lead={selected}
                template={template}
                pending={awaitingAwareness}
                contact={selected.contacts[0] ?? null}
                sender={user}
                stages={settings.allStageDefs}
                currentStage={stageDef}
                onAdvance={(key) => void handleStageChange(key)}
                onOpenAdmin={onOpenAdmin}
              />

              {selected.nextTask && !selected.nextTask.doneAt && (
                <NextFollowUpCard task={selected.nextTask} onComplete={handleComplete} />
              )}
            </div>
          </div>

          <div className="drawer__more">
            {selected.guides.length > 0 && (
              <Disclosure
                title="Skida PRO roster"
                count={selected.guides.length}
                hint={`${selected.guides.filter((g) => g.signedIn).length} signed in`}
              >
                <GuideStatusTable guides={selected.guides} />
              </Disclosure>
            )}

            <Disclosure
              title="Qualification"
              count={`${qualificationScore(selected.qualification)}/3`}
              hint={scoreStyle(qualificationScore(selected.qualification)).label}
            >
              <QualificationCard
                qualification={selected.qualification}
                onEdit={() => setShowQual(true)}
              />
            </Disclosure>

            {selected.prospectingNotes && (
              <Disclosure title="Research notes" hint="how this lead was found">
                <div className="prospecting">{selected.prospectingNotes}</div>
              </Disclosure>
            )}

            <Disclosure
              title="Sources"
              count={selected.enrichment.evidence.length || undefined}
              hint={selected.enrichment.source}
            >
              <EnrichmentCard enrichment={selected.enrichment} />
            </Disclosure>
          </div>
      </>
    ) : null

  /** Where it opens: inside the row, or in a drawer over everything. */
  const inlineDetail = viewMode === 'table' && !isMobile

  return (
    <main
      className="leadspane"
      onClick={() => {
        setDrawerOpen(false)
        setShowColumnPicker(false)
        setOpenFilter(null)
      }}
    >
      {/*
        Above the toolbar, where the prototype put the sales-goal card.

        That card is gone as of 4 Sep 2026 — the screen is read to work a list,
        and a season progress bar was taking the top of every visit to say
        something nobody was acting on there. The goal figures still exist; they
        are simply not what this screen is for. `SalesGoal.tsx` and its
        repository went with it rather than being left as an unrendered
        component somebody would later have to work out the status of.

        What stays is collapsible, for the same reason: the table is the screen.
      */}
      <div onClick={(e) => e.stopPropagation()}>
        <SeatsByStage leads={leads} stages={settings.allStageDefs} />
      </div>

      <div className="leadspane__toolbar" onClick={(e) => e.stopPropagation()}>
        <input
          className="input leadspane__search"
          data-onb="crm-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search, or try: mine, unassigned, won, 3/3…"
          aria-label="Search leads"
        />
        {/* Off-script 7: "1 leads". A count line is read on every visit, and a
            plural that does not agree with its number is the sort of thing that
            makes a reader distrust the number. */}
        <div className="leadspane__count">
          {filtered.length} {filtered.length === 1 ? 'lead' : 'leads'}
        </div>

        <button
          className="chip"
          aria-pressed={mineOnly}
          onClick={() => setMineOnly(!mineOnly)}
          title="Only leads assigned to you"
        >
          Mine
        </button>

        {/* A filter set on a column that is then hidden would otherwise keep
            narrowing the table with no visible control. */}
        {activeFilterCount > 0 && (
          <button className="chip leadspane__clearfilters" onClick={() => setFilters({})}>
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} · clear
          </button>
        )}

        {/* Never silently drop rows: say how many are held back, and offer the
            way in. Only shown when there is actually something behind it. */}
        {unverifiedCount > 0 && !showHidden && (
          <button
            className="chip leadspane__hidden"
            onClick={() => setShowHidden(true)}
            title="Leads waiting to be verified into the pipeline"
          >
            Step 0 · {unverifiedCount} to verify
          </button>
        )}
        {showHidden && (
          <button className="chip leadspane__hidden" onClick={() => setShowHidden(false)}>
            Step 0 · hide unverified
          </button>
        )}

        <div className="leadspane__colwrap">
          {/* Hidden on a phone: it configures columns the card list does not
              render, so every choice in it would appear to do nothing. */}
          {!isMobile && (
            <button
              className="btn btn--ghost"
              onClick={() => setShowColumnPicker((v) => !v)}
              aria-expanded={showColumnPicker}
            >
              ⚙ Columns
            </button>
          )}
          {showColumnPicker && (
            <ColumnPicker
              columns={columns}
              onToggle={toggleColumn}
              onClose={() => setShowColumnPicker(false)}
            />
          )}
        </div>

        <button
          className="btn btn--primary"
          data-onb="crm-newlead"
          onClick={() => setShowNewLead(true)}
        >
          + New lead
        </button>

        {/* Last in the row on every list screen in the design, not floating in
            the middle of the filters. */}
        <ViewToggle value={viewMode} onChange={setViewMode} label="Leads" withMap />
      </div>

      {viewMode === 'map' ? (
        <ListMap
          records={mapRecords}
          onOpen={(id) => setSelectedId(id)}
          emptyNote="No lead matches these filters, so there is nothing to place."
        />
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          columns={settings.allStageDefs
            .filter((st) => st.key !== 'unverified' && st.key !== 'archived')
            .map((st) => ({ key: st.key, label: st.label, color: st.color }))}
          cards={filtered.map((l) => toKanbanCard(l, lastActivity.get(l.id)))}
          /**
           * Lanes — decision 102. The pool first, then every CRM user who owns
           * something, and the signed-in user even when they own nothing: a
           * board you cannot give work to yourself on is missing the lane you
           * reach for most.
           */
          lanes={laneList}
          onMove={(id, to) => void reassign(id, to)}
          selectedId={drawerOpen ? selectedId : null}
          onSelect={(id) => {
            setSelectedId(id)
            setDrawerOpen(true)
          }}
          emptyNote={
            search.trim()
              ? `No lead matches “${search.trim()}”. Try the company name, a country, or an owner.`
              : 'No leads in the pipeline yet. Import a prospect list, or add one by hand.'
          }
        />
      ) : (
      <LeadTable
        rows={rows}
        columns={columns}
        selectedId={drawerOpen ? selectedId : null}
        expandedId={inlineDetail && drawerOpen ? selectedId : null}
        renderExpanded={() => <div className="leaddetail">{detailBody}</div>}
        /*
         * A second click on an open row shuts it again. Once the detail is
         * inside the row there is no scrim to click away and the ✕ is at the
         * far end of a tall panel, so the row itself has to be the way back —
         * the same thing that opened it.
         */
        onSelect={(id) => {
          const closing = inlineDetail && drawerOpen && selectedId === id
          setSelectedId(id)
          setDrawerOpen(!closing)
          setShowColumnPicker(false)
          setOpenFilter(null)
        }}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        loading={loading}
        search={search}
        emptyNote={
          unverifiedCount > 0 ? (
            <>
              Nothing in the working pipeline — {unverifiedCount} lead
              {unverifiedCount === 1 ? ' is' : 's are'} still in <strong>Step 0</strong>, waiting to
              be verified.{' '}
              <button className="linkbtn" onClick={() => setShowHidden(true)}>
                Start verifying
              </button>
            </>
          ) : undefined
        }
        filters={filters}
        filterOptions={filterOptions}
        onToggleFilter={toggleFilter}
        onClearFilter={clearFilter}
        openFilter={openFilter}
        onOpenFilter={setOpenFilter}
        onVerify={(id) => void handleVerify(id)}
        onArchive={(id) => void handleArchive(id)}
        busyId={rowBusy}
      />
      )}

      {drawerOpen && !inlineDetail && selected && detailBody && (
        <LeadDrawer
          title={selected.name}
          logoUrl={selected.logoUrl}
          onClose={() => setDrawerOpen(false)}
        >
          {detailBody}
        </LeadDrawer>
      )}

      {showNewLead && (
        <NewLeadModal
          stages={settings.allStageDefs}
          defaultOwner={user?.displayName ?? 'Unassigned'}
          onClose={() => setShowNewLead(false)}
          onCreate={handleCreateLead}
        />
      )}

      {showQual && selected && (
        <QualificationModal
          companyName={selected.name}
          qualification={selected.qualification}
          onClose={() => setShowQual(false)}
          onSave={handleSaveQualification}
        />
      )}

      {contactTarget && selected && (
        <ContactModal
          companyName={selected.name}
          contact={contactTarget.index === null ? null : (selected.contacts[contactTarget.index] ?? null)}
          onClose={() => setContactTarget(null)}
          onSave={handleSaveContact}
        />
      )}
    </main>
  )
}
